// API 응답 → 컴포넌트 모델 변환

export const STATUS_MAP = {
  SUBMITTED: '지원완료',
  PASS_1: '1차통과',
  PASS_2: '2차통과',
  PASS_3: '3차통과',
  PASS_4: '4차통과',
  HIRED: '합격',
  REJECTED: '불합격',
  WITHDRAWN: '취소',
};

export const STATUS_LABEL = {
  SUBMITTED: '지원 완료',
  PASS_1: '1차 통과', PASS_2: '2차 통과', PASS_3: '3차 통과', PASS_4: '4차 통과',
  HIRED: '최종 합격',
  REJECTED: '불합격',
  WITHDRAWN: '지원 취소',
};

const STATUS_DESC = {
  SUBMITTED: '기본 프로필 및 자기소개 제출',
  PASS_1: '1차 심사 통과', PASS_2: '2차 심사 통과', PASS_3: '3차 심사 통과', PASS_4: '4차 심사 통과',
  HIRED: '최종 합격 확정',
  REJECTED: '심사 결과 불합격 처리되었습니다.',
  WITHDRAWN: '지원이 취소되었습니다.',
};

/** ISO 날짜 문자열 → "YYYY.MM.DD HH:mm" (타임존 변환 없이 서버값 그대로) */
export function fmtDateTime(isoStr) {
  if (!isoStr) return '';
  const date = isoStr.slice(0, 10).replace(/-/g, '.');
  const timePart = isoStr.length >= 16 ? isoStr.slice(11, 16) : '';
  return timePart ? `${date} ${timePart}` : date;
}

/** GET /api/applications/{id} 응답 → DetailHeader 모델 */
export function mapAppFromDetail(id, detail, navState) {
  return {
    id,
    castingPostId: detail?.castingPostId || navState?.castingPostId || null,
    title: detail?.castingPostTitle || navState?.title || '-',
    category: detail?.projectType || detail?.category || navState?.category || '',
    status: STATUS_MAP[detail?.status] || navState?.status || '-',
    company: detail?.companyName || navState?.company || '-',
    companyId: detail?.producerProfileId || null,
    role: detail?.roleName || navState?.role || '-',
    date: detail?.submittedAt
      ? detail.submittedAt.slice(0, 10).replace(/-/g, '. ')
      : navState?.date || '-',
  };
}

/** GET /api/applications/{id} 응답 → DetailSubmission 모델 */
export function mapSubmissionFromDetail(detail) {
  const files = Array.isArray(detail?.attachments)
    ? detail.attachments.map(f => ({
        id: f.fileEntityId ?? null,
        name: f.originalName || '첨부파일',
        url: f.url || null,
        type: f.fileType || '',
        size: f.sizeBytes ? `${Math.round(f.sizeBytes / 1024)}KB` : '',
      }))
    : [];

  const videoLink = detail?.videoLink;
  const videos = videoLink
    ? [{
        url: videoLink,
        title: videoLink,
        platform: videoLink.includes('youtube.com') || videoLink.includes('youtu.be')
          ? 'YouTube'
          : videoLink.includes('vimeo.com')
            ? 'Vimeo'
            : '링크',
      }]
    : [];

  return { intro: detail?.message || '', files, videos };
}

/** GET /api/castings/{id} 응답 → DetailAnnouncement 모델 */
export function mapAnnouncementFromCasting(casting) {
  const role = casting.roles?.[0];

  const isClosed =
    casting.status === 'closed' ||
    casting.status === 'CLOSED' ||
    (casting.applicationDeadline && casting.applicationDeadline !== '미정'
      ? new Date(casting.applicationDeadline) < new Date()
      : false);

  return {
    roleDesc: role?.description || casting.synopsis || '-',
    period: casting.applicationDeadline
      ? `지원 마감: ${casting.applicationDeadline}`
      : '추후 공지',
    location: [casting.regionProvince, casting.regionCity].filter(Boolean).join(' ') || '전국',
    ageRange:
      role?.ageMin != null && role?.ageMax != null
        ? `${role.ageMin}~${role.ageMax}세`
        : '제한 없음',
    height: '-',
    skills: [],
    language: '-',
    episodeCount: casting.category || casting.projectType || '-',
    fee: '협의 가능',
    isClosed,
  };
}

/** 지원 상세 응답 → DetailTimeline 아이템 배열 */
export function buildTimeline(detail) {
  // timeline, statusHistory 둘 다 동일 데이터로 응답됨
  const raw = detail?.timeline ?? detail?.statusHistory ?? null;

  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map(entry => {
      const status = entry.toStatus;
      return {
        label: entry.stageNameSnapshot || STATUS_LABEL[status] || status,
        desc: entry.comment || STATUS_DESC[status] || '',
        date: fmtDateTime(entry.changedAt),
        dueDate: entry.dueDate || null,
      };
    });
  }

  // fallback: timeline 이력 없을 때
  const status = detail?.status;
  const dateStr = fmtDateTime(detail?.submittedAt);
  if (status === 'REJECTED') {
    return [
      { label: '지원 완료', desc: STATUS_DESC.SUBMITTED, date: dateStr },
      { label: '불합격',    desc: STATUS_DESC.REJECTED,  date: dateStr },
    ];
  }
  if (status === 'WITHDRAWN') {
    return [
      { label: '지원 완료', desc: STATUS_DESC.SUBMITTED, date: dateStr },
      { label: '지원 취소', desc: STATUS_DESC.WITHDRAWN, date: dateStr },
    ];
  }
  const ORDER = ['SUBMITTED', 'PASS_1', 'PASS_2', 'PASS_3', 'PASS_4', 'HIRED'];
  const idx = ORDER.indexOf(status);
  return ORDER.slice(0, idx >= 0 ? idx + 1 : 1).map(s => ({
    label: STATUS_LABEL[s], desc: STATUS_DESC[s], date: dateStr,
  }));
}
